/**
 * Webchat Widget Embed Script
 *
 * Injects a launcher bubble + iframe onto any third-party page.
 * Fetches display config from the public-config endpoint so positioning
 * and theme always match what is configured in the admin UI.
 *
 * Usage:
 * <script
 *   src="https://console.orangepill.cloud/webchat/embed.js"
 *   data-entrypoint-id="<uuid>"
 * ></script>
 *
 * Optional overrides (take precedence over remote config):
 *   data-display-mode="bottom-right|bottom-left|center-modal|fullscreen|pane-left|pane-right"
 *
 * ADR-103: WC-specific DOM logic is delegated to WooCommerceSurfaceAdapter.
 * embed.ts is the thin loader: orchestration only, no platform-specific selectors.
 */

import { WooCommerceSurfaceAdapter } from './adapters/woocommerce/WooCommerceSurfaceAdapter.js';

(function () {
  'use strict';

  // Capture synchronously — document.currentScript is null inside async callbacks
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) return;

  const entrypointId = script.getAttribute('data-entrypoint-id');
  if (!entrypointId) {
    console.error('[OrangePill] data-entrypoint-id is required on the embed script tag');
    return;
  }

  // Derive the base URL from the script src so this works on any environment
  function getBaseUrl(): string {
    try {
      return new URL(script!.src).origin;
    } catch {
      return window.location.origin;
    }
  }

  const baseUrl = getBaseUrl();

  // ── OrangepillWebchat public API ─────────────────────────────────────────
  // Session ID is captured when WEBCHAT_LOADED bubbles up from the outer iframe.
  let activeConversationId: string | null = null;
  (window as any).OrangepillWebchat = {
    getActiveConversationId: () => activeConversationId,
  };

  // ── PR-SA-3: Surface context detection (ADR-103: delegated to WooCommerceSurfaceAdapter) ───
  // Iframe reference captured after creation — used to relay surface context.
  let activeIframe: HTMLIFrameElement | null = null;

  // Deep-equal dedupe: skip emission when page state is unchanged.
  let lastEmittedContext: string | null = null;

  // ADR-103: platform adapter — all WC-specific DOM detection/action logic lives here.
  const surfaceAdapter = new WooCommerceSurfaceAdapter();



  let surfaceDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  function emitSurfaceContext(): void {
    if (!activeIframe?.contentWindow) return;
    const ctx = surfaceAdapter.detectSurfaceContext();
    // PR-SA-6: Dedupe key extended with cartContext.itemCount to re-emit after add/remove.
    const key = `${ctx.pageType}|${ctx.url}|${ctx.productId ?? ''}|${ctx.checkoutContext?.step ?? ''}|${ctx.cartContext?.itemCount ?? ''}`;
    if (key === lastEmittedContext) return;
    lastEmittedContext = key;
    // Use '*' target — WEBCHAT_PAGE_CONTEXT carries only read-only page metadata (no secrets).
    // baseUrl may not match the iframe's actual origin in dev when the admin SPA is on a
    // different port from the widget iframe, causing a target-origin mismatch error.
    activeIframe.contentWindow.postMessage(
      { type: 'WEBCHAT_PAGE_CONTEXT', payload: ctx },
      '*',
    );
  }

  function scheduleSurfaceEmit(): void {
    if (surfaceDebounceTimer !== null) clearTimeout(surfaceDebounceTimer);
    surfaceDebounceTimer = setTimeout(emitSurfaceContext, 200);
  }

  // SPA navigation: patch history API and listen for popstate
  (function patchHistoryNav() {
    const origPush = history.pushState.bind(history);
    const origReplace = history.replaceState.bind(history);
    history.pushState = function (...args) {
      origPush(...args);
      scheduleSurfaceEmit();
    };
    history.replaceState = function (...args) {
      origReplace(...args);
      scheduleSurfaceEmit();
    };
    window.addEventListener('popstate', scheduleSurfaceEmit);
  })();
  // ── End PR-SA-3 surface detection ─────────────────────────────────────────

  // ── Cart hash deduplication (customer-page side) ─────────────────────────
  let lastCartHash: string | null | undefined = undefined;

  // ADR-103: register platform-native update signals (wc_fragments_refreshed, checkout MutationObserver).
  surfaceAdapter.listenForUpdates(scheduleSurfaceEmit);

  // ── Message listener: captures session ID + routes cart events ───────────
  window.addEventListener('message', (event: MessageEvent) => {
    if (!event.data || typeof event.data.type !== 'string') return;

    switch (event.data.type) {
      case 'WEBCHAT_LOADED': {
        const sessionId = event.data.payload?.sessionId;
        if (sessionId) activeConversationId = sessionId;
        // PR-SA-3: outer iframe is now ready to receive postMessages — emit surface context.
        // Reset dedup: the initial scheduleSurfaceEmit() at T+200ms fires before bootstrap.ts
        // messageHandler is registered, so the postMessage is lost but lastEmittedContext IS
        // set. Without this reset, the WEBCHAT_LOADED-triggered emit sees the same key and
        // short-circuits — surface context is never sent.
        lastEmittedContext = null;
        scheduleSurfaceEmit();
        break;
      }
      case 'WEBCHAT_CART_EVENT': {
        const cartHash: string | null = event.data.payload?.cartHash ?? null;
        if (cartHash !== null && cartHash === lastCartHash) break; // no change
        lastCartHash = cartHash;
        surfaceAdapter.triggerCartRefresh();
        break;
      }
      case 'WEBCHAT_SURFACE_ACTION': {
        // PR-SA-2: SURFACE-DELIVERY-001 — navigate the customer's page to the composed URL.
        // Origin guard: only accept from the gateway iframe (same baseUrl).
        if (event.origin !== baseUrl) break;
        const action = event.data.payload?.action;
        const url: string | undefined = event.data.payload?.url;
        if (!url || !action) break;
        surfaceAdapter.executeNavigationAction(action, url);
        break;
      }
      case 'WEBCHAT_CHECKOUT_ACTION': {
        // PR-SA-5.1.1: Delegate to adapter — WC button selection is adapter's concern.
        // Origin guard: only accept from the gateway iframe (same baseUrl).
        if (event.origin !== baseUrl) break;
        const action: string | undefined = event.data.payload?.action;
        if (!action) break;
        surfaceAdapter.executeCheckoutAction(action as any);
        break;
      }
    }
  });

  // ── Bridge token: fetch from WC plugin and post to outer iframe ──────────
  // Send immediately (succeeds if iframe message handler is already up) then
  // retry at 1 s and 3 s — React SPA takes 1–3 s to mount after iframe creation.
  // bootstrap.ts only forwards WEBCHAT_LOADED back to us after customerPageOrigin
  // is set, so we cannot rely on that signal; timer retries break the dependency.
  async function fetchAndPostBridgeToken(outerIframe: HTMLIFrameElement): Promise<void> {
    try {
      const res = await fetch('/wp-json/orangepill/v1/context', { credentials: 'include' });
      if (!res.ok || res.status === 204) return;
      const data = await res.json();
      // Plugin returns { bridgeToken, expiresIn } — field is "bridgeToken", not "wcBridgeToken"
      const wcBridgeToken: string | undefined = data?.bridgeToken ?? data?.wcBridgeToken;
      if (!wcBridgeToken) return;
      // Never persist: token travels only through postMessage, never to storage.
      const send = () => outerIframe.contentWindow?.postMessage({ type: 'BRIDGE_TOKEN_AVAILABLE', wcBridgeToken }, baseUrl);
      send();
      setTimeout(send, 1000);
      setTimeout(send, 3000);
    } catch {
      // Not a WC page or plugin not active — silently ignore.
    }
  }
  const identityToken = script.getAttribute('data-identity-token') || null;
  const webchatUrl = identityToken
    ? `${baseUrl}/webchat?entrypointId=${entrypointId}&identityToken=${encodeURIComponent(identityToken)}`
    : `${baseUrl}/webchat?entrypointId=${entrypointId}`;
  const configUrl = `${baseUrl}/v4/webchat/entrypoints/${entrypointId}/public-config`;
  // data-display-mode on the script tag overrides the remote value
  const displayModeOverride = script.getAttribute('data-display-mode') || null;

  interface PublicConfig {
    displayMode: string;
    widgetPosition: string;
    displayName: string | null;
    brandColor: string | null;
    welcomeMessage: string | null;
    paneWidth: string | null;
    mobileBreakpoint: number | null;
  }

  async function fetchConfig(): Promise<PublicConfig> {
    const defaults: PublicConfig = {
      displayMode: 'bottom-right',
      widgetPosition: 'bottom-right',
      displayName: 'Chat',
      brandColor: null,
      welcomeMessage: null,
      paneWidth: null,
      mobileBreakpoint: null,
    };
    try {
      const res = await fetch(configUrl);
      if (!res.ok) return defaults;
      const data = await res.json();
      return { ...defaults, ...data };
    } catch {
      return defaults;
    }
  }

  function getPosition(displayMode: string, widgetPosition: string): 'bottom-right' | 'bottom-left' {
    if (displayMode === 'bottom-left') return 'bottom-left';
    if (widgetPosition === 'bottom-left') return 'bottom-left';
    return 'bottom-right';
  }

  function createLauncher(
    position: 'bottom-right' | 'bottom-left',
    brandColor: string | null
  ): HTMLButtonElement {
    const btn = document.createElement('button');
    const color = brandColor || '#6366f1';
    const side = position === 'bottom-left' ? 'left: 20px;' : 'right: 20px;';
    btn.style.cssText = `
      position: fixed;
      bottom: 20px;
      ${side}
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: ${color};
      border: none;
      cursor: pointer;
      box-shadow: 0 4px 16px rgba(0,0,0,0.20);
      z-index: 999998;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.15s ease, box-shadow 0.15s ease;
    `;
    btn.setAttribute('aria-label', 'Open chat');
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
      fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>`;
    btn.addEventListener('mouseenter', () => {
      btn.style.transform = 'scale(1.08)';
      btn.style.boxShadow = '0 6px 20px rgba(0,0,0,0.25)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.transform = 'scale(1)';
      btn.style.boxShadow = '0 4px 16px rgba(0,0,0,0.20)';
    });
    return btn;
  }

  function createChatIframe(
    position: 'bottom-right' | 'bottom-left'
  ): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    const side = position === 'bottom-left' ? 'left: 20px;' : 'right: 20px;';
    iframe.style.cssText = `
      position: fixed;
      bottom: 90px;
      ${side}
      width: 400px;
      height: 600px;
      max-width: calc(100vw - 40px);
      max-height: calc(100vh - 110px);
      border: none;
      border-radius: 16px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.18);
      z-index: 999999;
      background: white;
      display: none;
      transition: opacity 0.2s ease, transform 0.2s ease;
      opacity: 0;
      transform: translateY(8px);
    `;
    iframe.setAttribute('title', 'Webchat');
    iframe.setAttribute('allow', 'microphone; camera');
    iframe.src = webchatUrl;
    return iframe;
  }

  function createFullscreenIframe(): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = `
      position: fixed;
      top: 0; left: 0;
      width: 100vw;
      height: 100vh;
      border: none;
      z-index: 999999;
      background: white;
    `;
    iframe.setAttribute('title', 'Webchat');
    iframe.setAttribute('allow', 'microphone; camera');
    iframe.src = webchatUrl;
    return iframe;
  }

  function createModalOverlay(): HTMLDivElement {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0; left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0,0,0,0.45);
      backdrop-filter: blur(4px);
      z-index: 999998;
    `;
    return overlay;
  }

  function createModalIframe(): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 450px;
      height: 650px;
      max-width: calc(100vw - 40px);
      max-height: calc(100vh - 40px);
      border: none;
      border-radius: 18px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.25);
      z-index: 999999;
      background: white;
    `;
    iframe.setAttribute('title', 'Webchat');
    iframe.setAttribute('allow', 'microphone; camera');
    iframe.src = webchatUrl;
    return iframe;
  }

  // ── Pane mode helpers ────────────────────────────────────────────────────

  function setPaneCssVar(width: string) {
    document.documentElement.style.setProperty('--op-pane-width', width);
    document.documentElement.classList.add('op-pane-open');
  }

  function clearPaneCssVar() {
    document.documentElement.style.setProperty('--op-pane-width', '0px');
    document.documentElement.classList.remove('op-pane-open');
  }

  function createPaneIframe(side: 'pane-left' | 'pane-right', width: string): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    const isLeft = side === 'pane-left';
    const translateOut = isLeft ? 'translateX(-100%)' : 'translateX(100%)';
    iframe.style.cssText = `
      position: fixed;
      top: 0;
      ${isLeft ? 'left: 0;' : 'right: 0;'}
      width: ${width};
      height: 100vh;
      border: none;
      z-index: 999999;
      background: white;
      box-shadow: ${isLeft ? '4px 0 24px rgba(0,0,0,0.15)' : '-4px 0 24px rgba(0,0,0,0.15)'};
      transform: ${translateOut};
      transition: transform 0.25s ease;
    `;
    iframe.setAttribute('title', 'Webchat');
    iframe.setAttribute('allow', 'microphone; camera');
    iframe.src = webchatUrl;
    return iframe;
  }

  function createPaneTab(side: 'pane-left' | 'pane-right', brandColor: string | null): HTMLButtonElement {
    const tab = document.createElement('button');
    const color = brandColor || '#6366f1';
    const isLeft = side === 'pane-left';
    tab.style.cssText = `
      position: fixed;
      top: 50%;
      ${isLeft ? 'left: 0;' : 'right: 0;'}
      transform: translateY(-50%)${isLeft ? '' : ''};
      width: 40px;
      height: 80px;
      background: ${color};
      border: none;
      border-radius: ${isLeft ? '0 8px 8px 0' : '8px 0 0 8px'};
      cursor: pointer;
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: ${isLeft ? '2px 2px 8px rgba(0,0,0,0.2)' : '-2px 2px 8px rgba(0,0,0,0.2)'};
      transition: width 0.15s ease, box-shadow 0.15s ease;
    `;
    tab.setAttribute('aria-label', 'Open chat');
    tab.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
      fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>`;
    tab.addEventListener('mouseenter', () => { tab.style.width = '46px'; tab.style.boxShadow = isLeft ? '3px 3px 12px rgba(0,0,0,0.25)' : '-3px 3px 12px rgba(0,0,0,0.25)'; });
    tab.addEventListener('mouseleave', () => { tab.style.width = '40px'; tab.style.boxShadow = isLeft ? '2px 2px 8px rgba(0,0,0,0.2)' : '-2px 2px 8px rgba(0,0,0,0.2)'; });
    return tab;
  }

  // ── End pane helpers ─────────────────────────────────────────────────────

  async function run() {
    const config = await fetchConfig();
    const displayMode = displayModeOverride || config.displayMode;
    const paneWidth = config.paneWidth || '25%';
    const mobileBreakpoint = config.mobileBreakpoint ?? 768;

    if (displayMode === 'fullscreen') {
      const iframe = createFullscreenIframe();
      document.body.appendChild(iframe);
      fetchAndPostBridgeToken(iframe);
      activeIframe = iframe;
      scheduleSurfaceEmit();
      return;
    }

    if (displayMode === 'center-modal') {
      const overlay = createModalOverlay();
      const iframe = createModalIframe();
      let open = false;

      const position = getPosition(displayMode, config.widgetPosition);
      const launcher = createLauncher(position, config.brandColor);
      fetchAndPostBridgeToken(iframe);
      activeIframe = iframe;
      scheduleSurfaceEmit();

      function openModal() {
        open = true;
        document.body.appendChild(overlay);
        document.body.appendChild(iframe);
        launcher.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
          viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>`;
      }

      function closeModal() {
        open = false;
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        launcher.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
          viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>`;
      }

      launcher.addEventListener('click', () => (open ? closeModal() : openModal()));
      overlay.addEventListener('click', closeModal);
      document.body.appendChild(launcher);
      return;
    }

    if (displayMode === 'pane-left' || displayMode === 'pane-right') {
      const side = displayMode as 'pane-left' | 'pane-right';
      const isMobile = window.matchMedia(`(max-width: ${mobileBreakpoint}px)`).matches;

      if (isMobile) {
        // Degrade gracefully to bottom-right launcher on mobile
        const fallbackPosition = 'bottom-right';
        const launcher = createLauncher(fallbackPosition, config.brandColor);
        const chatIframe = createChatIframe(fallbackPosition);
        let open = false;
        function openChat() {
          open = true;
          chatIframe.style.display = 'block';
          requestAnimationFrame(() => { chatIframe.style.opacity = '1'; chatIframe.style.transform = 'translateY(0)'; });
          launcher.setAttribute('aria-label', 'Close chat');
          launcher.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
        }
        function closeChat() {
          open = false;
          chatIframe.style.opacity = '0';
          chatIframe.style.transform = 'translateY(8px)';
          setTimeout(() => { chatIframe.style.display = 'none'; }, 200);
          launcher.setAttribute('aria-label', 'Open chat');
          launcher.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;
        }
        launcher.addEventListener('click', () => (open ? closeChat() : openChat()));
        document.body.appendChild(launcher);
        document.body.appendChild(chatIframe);
        fetchAndPostBridgeToken(chatIframe);
        activeIframe = chatIframe;
        scheduleSurfaceEmit();
        return;
      }

      const paneIframe = createPaneIframe(side, paneWidth);
      const tab = createPaneTab(side, config.brandColor);
      const isLeft = side === 'pane-left';
      const translateOut = isLeft ? 'translateX(-100%)' : 'translateX(100%)';
      const marginProp = isLeft ? 'marginLeft' : 'marginRight';
      let open = false;

      // Body push: shift page content so the pane sits alongside it rather than over it.
      // We always inject — overflow-x:hidden on body (common in WooCommerce themes) does not
      // prevent margin from working; content just reflows within the narrower width.
      function pushBody() {
        document.body.style.transition = `${isLeft ? 'margin-left' : 'margin-right'} 0.25s ease`;
        document.body.style[marginProp] = paneWidth;
      }

      function releaseBody() {
        document.body.style[marginProp] = '';
      }

      const PANE_SESSION_KEY = 'op_pane_open';

      function openPane() {
        open = true;
        paneIframe.style.transform = 'translateX(0)';
        setPaneCssVar(paneWidth);
        pushBody();
        sessionStorage.setItem(PANE_SESSION_KEY, '1');
        tab.setAttribute('aria-label', 'Close chat');
        tab.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
      }

      function closePane() {
        open = false;
        paneIframe.style.transform = translateOut;
        clearPaneCssVar();
        releaseBody();
        sessionStorage.removeItem(PANE_SESSION_KEY);
        tab.setAttribute('aria-label', 'Open chat');
        tab.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;
      }

      tab.addEventListener('click', () => (open ? closePane() : openPane()));
      document.body.appendChild(paneIframe);
      document.body.appendChild(tab);
      fetchAndPostBridgeToken(paneIframe);
      activeIframe = paneIframe;
      scheduleSurfaceEmit();

      // Re-open immediately if it was open when the user navigated to this page.
      // sessionStorage is tab-scoped: persists across navigations, cleared on tab close.
      if (sessionStorage.getItem(PANE_SESSION_KEY) === '1') {
        openPane();
      }

      // Respond to viewport resize: collapse pane and degrade to bottom-right if needed
      const mq = window.matchMedia(`(max-width: ${mobileBreakpoint}px)`);
      mq.addEventListener('change', (e) => {
        if (e.matches) {
          // Remove pane elements, fall back handled on next page load
          clearPaneCssVar();
          releaseBody();
          sessionStorage.removeItem(PANE_SESSION_KEY);
          if (paneIframe.parentNode) paneIframe.parentNode.removeChild(paneIframe);
          if (tab.parentNode) tab.parentNode.removeChild(tab);
        }
      });

      return;
    }

    // bottom-right or bottom-left (default)
    const position = getPosition(displayMode, config.widgetPosition);
    const launcher = createLauncher(position, config.brandColor);
    const chatIframe = createChatIframe(position);
    let open = false;

    function openChat() {
      open = true;
      chatIframe.style.display = 'block';
      // Trigger CSS transition
      requestAnimationFrame(() => {
        chatIframe.style.opacity = '1';
        chatIframe.style.transform = 'translateY(0)';
      });
      launcher.setAttribute('aria-label', 'Close chat');
      launcher.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
        viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>`;
    }

    function closeChat() {
      open = false;
      chatIframe.style.opacity = '0';
      chatIframe.style.transform = 'translateY(8px)';
      setTimeout(() => { chatIframe.style.display = 'none'; }, 200);
      launcher.setAttribute('aria-label', 'Open chat');
      launcher.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
        viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>`;
    }

    launcher.addEventListener('click', () => (open ? closeChat() : openChat()));
    document.body.appendChild(launcher);
    document.body.appendChild(chatIframe);
    fetchAndPostBridgeToken(chatIframe);
    activeIframe = chatIframe;
    scheduleSurfaceEmit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
